import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import LineItem from "./LineItem";
import SubmittedMessage from "./SubmittedMessage";

import { addLineItem, clearOrderSession, publishNewOrder, deleteLineItem } from '../../redux/actions/orderSessions';
import { scanInventory } from '../../redux/actions/inventorySessions';

const OrderCreation = ({ payload, orderSession, lineItems, accountUnits, postScanInventory, addAction, clearSession, submitOrder, removeLineItem }) => {
  const [submitted, setSubmitted] = useState(false);
  const [scanTerm, setScanTerm] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [lineItemValues, setLineItemValues] = useState({});
  const [lineItemTotals, setLineItemTotals] = useState({});
  const [totalChangeValues, setTotalChangeValues] = useState(0);
  const [totalPriceValue, setTotalPriceValue] = useState(0.0);

  const focus = (event) => {
    event.target.parentElement.classList.add("field--focus");
  }

  const blur = (event) => {
    event.target.parentElement.classList.remove("field--focus");
  }

  const publishOrderSession = () => {
    let submitCounts = [];

    lineItems.map((event, index) => {
      if(!(event.error)){
        event.modifyCount = lineItemValues[event.sku];
        submitCounts.push(event);
      }
    })

    let clearButton = document.getElementById("clear-button");
    let submitButton = document.getElementById("submit-button");

    clearButton.disabled = "true";
    submitButton.disabled = "true";

    submitOrder(payload, submitCounts);
    setSubmitted(true);
  }

  const updateScan = (event) => {
    let value = event.target.value;
    setScanTerm(value);
    if (value) {
      event.target.parentElement.classList.add("field--filled");
    } else {
      event.target.parentElement.classList.remove("field--filled");
    }
  }

  const setValue = (value, lineEvent) => {
    setLineItemTotals(lineItemTotals=>({
      ...lineItemTotals,
      [lineEvent.sku]: ((lineEvent?.price || lineEvent?.defaultDisplayedPrice || 0.0) * parseInt(value))
    }));

    setLineItemValues(lineItemValues=>({
      ...lineItemValues,
      [lineEvent.sku]: parseInt(value)
    }));
  }

  const setTotals = () => {
    let tempValue = 0;
    let tempTotal = 0.0;

    for (const [key, value] of Object.entries(lineItemValues)) {
      tempValue += (parseInt(value) || 0);
    }

    for (const [key, value] of Object.entries(lineItemTotals)) {
      tempTotal += (parseFloat(value) || 0);
    }

    setTotalChangeValues(tempValue);
    setTotalPriceValue(formatPriceValue(tempTotal));
  }

  const formatPriceValue = (value) => {
    return accountUnits.currencyPrefix + ' ' + Intl.NumberFormat().format(value) + ' ' + accountUnits.currencySuffix;
  }

  const lineItemsLoaded = () => {
    let firstLoading = lineItems.find((lineItem) => {
      return lineItem.placeholder
    });

    return !firstLoading;
  }

  const resetSession = () => {
    setSubmitted(false);
    clearSession();
    setLineItemValues({});
    setLineItemTotals({});
    setTotalChangeValues(0);
    setTotalPriceValue(formatPriceValue(0.0));
  }

  const removeItem = (lineItem, actionId) => {
    setValue(0, lineItem);
    setTotals();
    removeLineItem(actionId);
  }

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if(scanTerm !== ''){
        let matchingLine = lineItems.find(lineItem => { return (lineItem.sku === scanTerm) || (lineItem?.attributes?.find(attr => attr.name = 'UPC')?.value === scanTerm); });

        if(!matchingLine) {
          addAction(scanTerm, lineItems.length);
          postScanInventory(payload, scanTerm, lineItems.length);
        } else {
          setValue(lineItemValues[matchingLine.sku] + 1, matchingLine);
        }

        setScanTerm('');
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [scanTerm, 500]);

  useEffect(() => {
    setSubmitted(false);
    clearSession();
    flashScanBar();
  }, []);

  useEffect(() => {
    setTotals();
  }, [lineItemValues]);

  useEffect(() => {  
    if(orderSession.status === "success") {
      lineItems.map((lineItem) => {
        setValue(lineItemValues[lineItem.sku] || 1, lineItem);
      });
    }

    if (orderSession.status === "waiting" && !orderSession.errors) {
      setLineItemValues({});
      setLineItemTotals({});
      setTotalChangeValues(0);
      setTotalPriceValue(formatPriceValue(0.0));
      setTotals();
    }
  }, [orderSession]);

  const flashScanBar = () => {
    let scanBarElement = document.querySelector("#scan-bar");

    setTimeout(() => {
      scanBarElement?.classList?.add("blink-action");
    }, 500);

    setTimeout(() => {
      scanBarElement?.classList?.remove("blink-action");
    }, 1500);
  }

  return(
    <>
      <PageHeader stripeBackground='#39a0a0' helpLink="ecwid/create-order/" />
      <div className="settings-page cf">
        <div className="settings-page__header">
          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Create Order</div>
              </div>
              <div className="named-area__description">
                <p>Select the "Scan Barcode" bar and scan a generated Code 128 SKU (the generated barcodes from the "Labels + Print" section of this app, or similar) with a barcode scanner. This scan should also work with UPCs, EANs or ISBN values if they have been set in products or variations.</p>
              </div>
            </div>
            <div className="named-area__body">

              <div className="a-card a-card--compact">
                <div className="a-card__paddings">
                  <div className="iconable-block iconable-block--hide-in-mobile">
                    <div className="iconable-block__infographics">
                      <span className="iconable-block__icon">
                        <svg width="18" height="18" fill="#189dee" viewBox="0 0 32 32" id="svg5" version="1.1">
                          <defs id="defs2"/>
                          <g id="layer1" transform="translate(-108,-100)">
                            <path d="m 111,106 a 1.0001,1.0001 0 0 0 -1,1 v 3 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -2 h 2 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 z" id="path11698" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 134,106 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 2 v 2 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -3 a 1.0001,1.0001 0 0 0 -1,-1 z" id="path11700" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 137,121 a 1,1 0 0 0 -1,1 v 2 h -2 a 1,1 0 0 0 -1,1 1,1 0 0 0 1,1 h 3 a 1.0001,1.0001 0 0 0 1,-1 v -3 a 1,1 0 0 0 -1,-1 z" id="path11702" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 111,121 a 1,1 0 0 0 -1,1 v 3 a 1.0001,1.0001 0 0 0 1,1 h 3 a 1,1 0 0 0 1,-1 1,1 0 0 0 -1,-1 h -2 v -2 a 1,1 0 0 0 -1,-1 z" id="path11704" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 115,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11706" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 118,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11708" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 121,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11710" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 124,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11712" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 127,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11714" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 130,110 a 1,1 0 0 0 -1,1 v 10 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 v -10 a 1,1 0 0 0 -1,-1 z" id="path11716" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                            <path d="m 133,110 a 1,1 0 0 0 -1,1 v 5.20703 1.31445 V 121 a 1,1 0 0 0 1,1 1,1 0 0 0 1,-1 V 117.52148 116.20703 111 a 1,1 0 0 0 -1,-1 z" id="path11720" style={{ 'fill-rule':'evenodd', 'stroke-linecap':'round', 'stroke-linejoin':'round', 'stroke-miterlimit':'4.1', '-inkscape-stroke':'none'}}/>
                          </g>
                        </svg>
                      </span>
                    </div>
                    <div className="iconable-block__content">
                      <div className="status-block">
                        <div className="status-block__central">
                          <div className="status-block__header">
                            <span className="status-block__title">Scan Barcode (SKU, UPC, EAN)</span>
                            <span className="status-block__edit">Edit</span>
                          </div>
                          <div className="status-block__content">
                            <div className="fieldset fieldset__max">
                              <div id="scan-bar" className="field field--large">
                                <span className="fieldset__svg-icon"></span>
                                <label className="field__label">Scan Value</label>

                                { !submitted && <input type="text" value={scanTerm} onFocus={focus} onBlur={blur} onChange={updateScan} className="field__input" tabindex="4" /> }
                                { submitted && <input type="text" value={scanTerm} onFocus={focus} onBlur={blur} onChange={updateScan} className="field__input" tabindex="4" disabled /> }

                                <div className="field__placeholder">Click here to begin scanning</div>
                                <span className="field-state--success"></span>
                                <span className="field-state--close"></span>
                              </div>
                              <div className="field__error" aria-hidden="true" style={{display: 'none'}}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <div className="named-area">
            <div className="named-area__header">
              <div className="named-area__titles">
                <div className="named-area__title">Line Items</div>
              </div>
              <div className="named-area__description">
                <p>When items are scanned, they should appear below. Add a value to the "Quantity" field for each item. When submitted, the quantity associated with the line item will be added to the created order.</p>
                <p>Scanning duplicate barcodes will increment the count of listed items.</p>
                <p>Click the "Submit Order" button to create the order. A link will appear to view the order once it has been created.</p>
                <p>Orders will be created with your store's default tax settings, and without shipping settings or a customer set.</p>
                <p>Inventory will be removed for the scanned items when an order is created.</p>
              </div>
            </div>
            <div className="named-area__body">
              { submitted && 
                <SubmittedMessage
                  payload={payload}
                  resetSession={resetSession}
                  formatPriceValue={formatPriceValue}
                />
              }

              <div className="a-card a-card--compact">
                <div className="a-card__paddings">

                  <div className="flex-container">
                    <b>Subtotal</b>: {totalPriceValue}
                    <div className="flex-container flex-end flex-1 left-15">
                      { !submitted && !orderSession.isFetching && <button id="clear-button" className="btn btn-default btn--destructive btn-medium flex-1 fit-content" onClick={() => resetSession()}>Clear session</button> }
                      { !submitted && orderSession.isFetching && <button id="clear-button" className="btn btn-default btn--destructive btn-medium flex-1 fit-content" disabled>Clear session</button> }
                      { submitted && <button id="clear-button" className="btn btn-default btn--destructive btn-medium flex-1 fit-content" disabled>Clear session</button> }

                      { !submitted && !orderSession.isFetching && (totalChangeValues > 0) && lineItemsLoaded() && <button id="submit-button" className="btn btn-primary btn-medium flex-1 fit-content left-15" onClick={() => publishOrderSession()}>Submit Order</button> }
                      { !submitted && (orderSession.isFetching || (totalChangeValues === 0) || !lineItemsLoaded()) && <button id="submit-button" className="btn btn-primary btn-medium flex-1 fit-content left-15" disabled>Submit Order</button> }
                      { submitted && !orderSession.orderId &&
                        <LoadingSpinner statusText="Creating Order" />
                      }
                      { submitted && orderSession.orderId &&
                        <div className="text-default muted left-15">Order Created</div>
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="filtered-list">
                <div className="filtered-list__items long-list">
                  { lineItems.toReversed().map((event, index) =>
                    <LineItem
                      lineItemValues={lineItemValues}
                      setValue={setValue}
                      actionId={lineItems.length - 1 - index}
                      submitted={submitted}
                      lineEvent={event}
                      removeItem={removeItem}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const mapStateToProps = state => ({
  accountUnits: state.accounts.account.store_details.formatsAndUnits,
  orderSession: state.orderSessions,
  lineItems: state.orderSessions.lineItems,
});

const mapDispatchToProps = (dispatch) => ({
  postScanInventory: (payload, barcode, actionId) => dispatch(scanInventory(payload, barcode, actionId)),
  addAction: (barcode, actionId) => dispatch(addLineItem(barcode, actionId)),
  clearSession: () => dispatch(clearOrderSession()),
  submitOrder: (payload, actions) => dispatch(publishNewOrder(payload, actions)),
  removeLineItem: (actionId) => dispatch(deleteLineItem(actionId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(OrderCreation);
